import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SceneFlowOption, SceneFlowScene } from '../types';
import {
  getGraphQLClient,
  GENERATE_SCENE_FLOW_OPTIONS,
  APPLY_SCENE_FLOW,
} from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Button, Card, Spinner, AutoResizeTextarea, Input } from '../components/common';

type GenerateResponse = {
  generateSceneFlowOptions: {
    success: boolean;
    errors: string[];
    options: SceneFlowOption[] | null;
  };
};

type ApplyResponse = {
  applySceneFlow: {
    success: boolean;
    errors: string[];
    chapter: { id: string } | null;
  };
};

export default function SceneFlowSelectionPage() {
  const { t } = useTranslation();
  const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>();
  const navigate = useNavigate();

  const [options, setOptions] = useState<SceneFlowOption[] | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [editedScenes, setEditedScenes] = useState<SceneFlowScene[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOptions = useCallback(async () => {
    if (!chapterId) return;
    setIsLoadingOptions(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<GenerateResponse>(GENERATE_SCENE_FLOW_OPTIONS, {
        chapterId,
      });
      const result = data.generateSceneFlowOptions;
      if (!result.success || !result.options) {
        setError(result.errors.join(', ') || t('common.error'));
        setOptions(null);
      } else {
        setOptions(result.options);
        setSelectedOptionId(result.options[0]?.id ?? null);
        setEditedScenes(
          (result.options[0]?.scenes ?? []).map((s) => ({ ...s, covers: [...s.covers] }))
        );
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsLoadingOptions(false);
    }
  }, [chapterId, t]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const selectOption = (optionId: string) => {
    const opt = options?.find((o) => o.id === optionId);
    if (!opt) return;
    setSelectedOptionId(optionId);
    setEditedScenes(opt.scenes.map((s) => ({ ...s, covers: [...s.covers] })));
  };

  const updateTitle = (idx: number, title: string) =>
    setEditedScenes((prev) => prev.map((s, i) => (i === idx ? { ...s, title } : s)));

  const updateDescription = (idx: number, description: string) =>
    setEditedScenes((prev) => prev.map((s, i) => (i === idx ? { ...s, description } : s)));

  const removeAt = (idx: number) =>
    setEditedScenes((prev) => prev.filter((_, i) => i !== idx));

  const move = (idx: number, direction: -1 | 1) => {
    setEditedScenes((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const apply = async () => {
    if (!chapterId || editedScenes.length === 0) return;
    setIsApplying(true);
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<ApplyResponse>(APPLY_SCENE_FLOW, {
        chapterId,
        scenes: editedScenes.map((s) => ({
          title: s.title,
          description: s.description,
          covers: s.covers,
        })),
      });
      const result = data.applySceneFlow;
      if (!result.success) {
        setError(result.errors.join(', ') || t('common.error'));
        return;
      }
      navigate(`/books/${bookId}/refine?generate=true`);
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsApplying(false);
    }
  };

  if (isLoadingOptions) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Spinner />
        <p className="text-gray-600 dark:text-gray-400">
          {t(
            'chaptersFirst.generatingSceneOptions',
            'Drafting three different scene structures...'
          )}
        </p>
      </div>
    );
  }

  if (error && !options) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex gap-2">
            <Button onClick={loadOptions}>{t('common.retry', 'Retry')}</Button>
            <Button variant="ghost" onClick={() => navigate(`/books/${bookId}/refine`)}>
              {t('chaptersFirst.backToWorkspace', 'Back to workspace')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!options) return null;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header>
        <button
          onClick={() => navigate(`/books/${bookId}/refine`)}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          ← {t('chaptersFirst.backToWorkspace', 'Back to workspace')}
        </button>
        <h1 className="text-2xl font-bold mb-2">
          {t('chaptersFirst.selectSceneStructure', 'Choose a scene structure')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t(
            'chaptersFirst.selectSceneStructureHelp',
            'Pick the breakdown that fits, then edit the scenes before confirming.'
          )}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((option) => {
          const isSelected = option.id === selectedOptionId;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => selectOption(option.id)}
              className={`text-left rounded-lg border p-4 transition ${
                isSelected
                  ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
              }`}
            >
              <div className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-1">
                {option.label}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{option.summary}</p>
              <ul className="space-y-1 text-sm">
                {option.scenes.map((s, i) => (
                  <li key={i} className="truncate">
                    <span className="text-gray-400 mr-1">{i + 1}.</span>
                    {s.title}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {editedScenes.length > 0 && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold mb-4">
            {t('chaptersFirst.editScenes', 'Edit scenes')}
          </h2>
          <ol className="space-y-4">
            {editedScenes.map((sub, idx) => (
              <li key={idx} className="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-gray-400 mt-2 w-6 text-right">{idx + 1}.</span>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={sub.title}
                      onChange={(e) => updateTitle(idx, e.target.value)}
                      placeholder={t('chaptersFirst.sceneTitle', 'Scene title')}
                    />
                    <AutoResizeTextarea
                      value={sub.description}
                      onChange={(e) => updateDescription(idx, e.target.value)}
                      placeholder={t(
                        'chaptersFirst.sceneDescription',
                        'What is this scene about?'
                      )}
                      minRows={2}
                    />
                    {sub.covers.length > 0 && (
                      <ul className="text-xs text-gray-500 list-disc pl-4">
                        {sub.covers.map((cover, ci) => (
                          <li key={ci}>{cover}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label={t('common.moveUp', 'Move up')}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => move(idx, 1)}
                      disabled={idx === editedScenes.length - 1}
                      aria-label={t('common.moveDown', 'Move down')}
                    >
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeAt(idx)}
                      aria-label={t('common.remove', 'Remove')}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {error && <p className="text-red-600">{error}</p>}

      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={loadOptions} disabled={isLoadingOptions || isApplying}>
          {t('chaptersFirst.regenerateOptions', 'Regenerate options')}
        </Button>
        <Button onClick={apply} isLoading={isApplying} disabled={editedScenes.length === 0}>
          {t('chaptersFirst.confirmScenes', 'Confirm scenes')}
        </Button>
      </div>
    </div>
  );
}
